<template>
    <div class="mx-auto container container--fluid">
        <v-snackbar v-model="snackbar" :top="true" :color="color" :timeout="5000">
            <span>{{ snackbar_msg }}</span>
            <v-btn dark text @click="snackbar = false">Close</v-btn>
        </v-snackbar>

        <v-card class="box">
            <v-toolbar dense class="toolbar" flat color="white">
                <v-toolbar-title>Student Attendance Report</v-toolbar-title>
                <v-divider class="mx-4" inset vertical></v-divider>
                <v-spacer></v-spacer>
            </v-toolbar>

            <div class="px-4 mt-4 mb-6">
                <table class="simple-exam-table">
                    <thead>
                        <tr>
                            <th>Exam Plan</th>
                            <th>Academic Year</th>
                            <th>Semester</th>
                            <th>Start Date</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr>
                            <td>{{ scheduleHeader.exam_name || '-' }}</td>
                            <td>{{ scheduleHeader.academic_year || '-' }}</td>
                            <td>{{ scheduleHeader.semester || '-' }}</td>
                            <td>{{ formatDate(scheduleHeader.start_date) }}</td>
                        </tr>
                    </tbody>
                    <thead>
                        <tr>
                            <th>End Date</th>
                            <th>Exam Conduct Type</th>
                            <th>Stream</th>
                            <th>Standard</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr>
                            <td>{{ formatDate(scheduleHeader.end_date) }}</td>
                            <td>{{ formatList(scheduleHeader.exam_conduct_types) }}</td>
                            <td>{{ formatList(scheduleHeader.streams) }}</td>
                            <td>{{ formatStandards(scheduleHeader.standards) }}</td>
                        </tr>
                    </tbody>
                </table>
            </div>

            <v-data-table
                :headers="headers"
                :items="attendanceReportList"
                :search="search"
                class="elevation-1 mytable custom-row-height"
                :loading="overlay"
            >
                <template v-slot:top>
                    <v-row dense class="px-2 pb-2" align="center">
                        <v-col cols="4" sm="3" md="3" class="mr-5">
                            <v-btn
                                rounded
                                color="success"
                                outlined
                                :loading="loader"
                                small
                                class="mt-2 ml-2"
                                @click="exportToExcel"
                            >
                                <v-icon left color="success">mdi-export</v-icon>
                                Export
                            </v-btn>

                            <v-btn
                                rounded
                                color="error"
                                outlined
                                small
                                class="mt-2 ml-2"
                                @click="exportToPDF"
                                :disabled="!attendanceReportList.length"
                            >
                                <v-icon left color="error">mdi-file-pdf-box</v-icon>
                                Export PDF
                            </v-btn>
                        </v-col>
                        <v-col sm="6" md="6" cols="12">
                            <v-text-field
                                v-model="search"
                                append-icon="search"
                                label="Search"
                                single-line
                                hide-details
                            ></v-text-field>
                        </v-col>
                    </v-row>
                </template>

                <template v-slot:item.sno="{ item }">
                    {{ attendanceReportList.indexOf(item) + 1 }}
                </template>

                <template v-slot:item.attendance_status="{ item }">
                    <span :class="item.attendance_status === 'P' ? 'green--text font-weight-bold' : 'red--text font-weight-bold'">
                        {{ item.attendance_status || '-' }}
                    </span>
                </template>
            </v-data-table>

            <v-overlay :value="overlay">
                <v-progress-circular indeterminate size="64"></v-progress-circular>
            </v-overlay>
        </v-card>
    </div>
</template>

<script>
import axios from "axios";
import * as XLSX from "xlsx";

export default {
    name: "StudentAttendanceReport",
    data: () => ({
        overlay: false,
        loader: false,
        snackbar: false,
        snackbar_msg: "",
        color: "",
        search: "",
        exam_plan_id: null,
        scheduleHeader: {},
        org_basic_data: [],
        attendanceReportList: [],
        headers: [
            { text: "S.No", align: "center", value: "sno", sortable: true },
            { text: "PRN", align: "center", value: "prn", sortable: true },
            { text: "Roll No", align: "center", value: "rollno", sortable: true },
            { text: "Student Name", align: "left", value: "student_name", sortable: true },
            { text: "Present(P)/Absent(A)", align: "center", value: "attendance_status", sortable: true },
            { text: "Session No", align: "center", value: "session_number", sortable: true },
            { text: "Exam Date", align: "center", value: "exam_date", sortable: true },
            { text: "Day", align: "center", value: "day", sortable: true },
            { text: "Slot No", align: "center", value: "slot_no", sortable: true },
            { text: "From Time", align: "center", value: "from_time", sortable: true },
            { text: "To Time", align: "center", value: "to_time", sortable: true },
            { text: "Subject Code", align: "center", value: "subject_code", sortable: true },
            { text: "Subject Name", align: "left", value: "subject_name", sortable: true },
            { text: "Conduct Type", align: "center", value: "conduct_type", sortable: true },
            { text: "Room No", align: "center", value: "room_number", sortable: true },
            { text: "Bench No", align: "center", value: "bench_no", sortable: true },
            { text: "Bench Side", align: "center", value: "bench_side", sortable: true },
            { text: "Supervisor", align: "left", value: "supervisor_name", sortable: true },
            { text: "Standard", align: "center", value: "standard", sortable: true },
            { text: "Division", align: "center", value: "division", sortable: true },
            { text: "Stream", align: "center", value: "stream", sortable: true },
            { text: "Exam Plan", align: "center", value: "exam_plan_name", sortable: true },
            { text: "Academic Year", align: "center", value: "academic_year", sortable: true },
            { text: "Semester", align: "center", value: "semester", sortable: true }
        ]
    }),

    mounted() {
        this.exam_plan_id = localStorage.getItem("exam_plan_id");
        this.loadReportData();
    },

    methods: {
        formatDate(dateStr) {
            if (!dateStr) return "-";
            const date = new Date(dateStr);
            return isNaN(date.getTime())
                ? dateStr
                : date.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
        },

        formatList(arr) {
            if (!arr || !arr.length) return "-";
            return arr.map(item => (typeof item === 'object' ? item.name : item)).filter(Boolean).join(", ");
        },

        formatStandards(arr) {
            if (!arr || !arr.length) return "-";
            return arr.map(item => (typeof item === 'object' ? item.standard : item)).filter(Boolean).join(", ");
        },

        loadReportData() {
            this.overlay = true;
            axios.post("/SeatingArrangement/getStudentAttendanceReportData", {
                exam_plan_id: Number(this.exam_plan_id)
            })
            .then(res => {
                if (res.data.msg === "200") {
                    this.scheduleHeader = res.data.schedule_data || {};
                    this.attendanceReportList = res.data.attendance_report_data || [];
                    this.org_basic_data = res.data.org_basic_data || [];
                } else {
                    this.showSnackbar("#b71c1c", res.data.text || "Failed to load attendance report.");
                }
            })
            .catch(err => {
                console.error("Attendance Fetch Error:", err);
                this.showSnackbar("#b71c1c", "Error fetching student attendance report.");
            })
            .finally(() => {
                this.overlay = false;
            });
        },

        exportToExcel() {
            this.loader = true;
            const data = this.attendanceReportList.map((item, idx) => ({
                "S.No": idx + 1,
                "PRN": item.prn,
                "Roll No": item.rollno,
                "Student Name": item.student_name,
                "Present(P)/Absent(A)": item.attendance_status,
                "Session No": item.session_number,
                "Exam Date": item.exam_date,
                "Day": item.day,
                "Slot No": item.slot_no,
                "From Time": item.from_time,
                "To Time": item.to_time,
                "Subject Code": item.subject_code,
                "Subject Name": item.subject_name,
                "Conduct Type": item.conduct_type,
                "Room No": item.room_number,
                "Bench No": item.bench_no,
                "Bench Side": item.bench_side,
                "Supervisor": item.supervisor_name,
                "Standard": item.standard,
                "Division": item.division,
                "Stream": item.stream,
                "Exam Plan": item.exam_plan_name,
                "Academic Year": item.academic_year,
                "Semester": item.semester
            }));

            const ws = XLSX.utils.json_to_sheet(data);
            const wb = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(wb, ws, "Student Attendance Report");
            XLSX.writeFile(wb, "Student_Attendance_Report.xlsx");
            this.loader = false;
        },

        exportToPDF() {
            if (!this.attendanceReportList.length) {
                this.showSnackbar("#b71c1c", "No data available to export.");
                return;
            }
            const org = this.org_basic_data || [];

            console.log("Organization data for PDF:", org);

        const escapeHtml = (value) => {
            return String(value == null ? "" : value)
                .replace(/&/g, "&amp;")
                .replace(/</g, "&lt;")
                .replace(/>/g, "&gt;")
                .replace(/"/g, "&quot;")
                .replace(/'/g, "&#39;");
        };

            const schoolName = escapeHtml(org.school_name || "");
            const trustName = escapeHtml(org.trust_name || "");
            const schoolLogo = org.school_logo || "";
            const trustLogo = org.trust_logo || "";

            const printWindow = window.open("", "_blank");

            if (!printWindow) {
                this.showSnackbar(
                    "#b71c1c",
                    "Please allow pop-ups to export the report"
                );
                return;
            }

            let html = `
                <html>
                <head>
                    <title>Student Attendance Report</title>
                    <style>
                        @page {
                            size: A4 landscape;
                            margin: 8mm;
                        }

                        body {
                            font-family: Arial, sans-serif;
                            font-size: 8px;
                            margin: 0;
                        }

                        h2 {
                            text-align: center;
                            margin: 0 0 8px 0;
                            font-size: 16px;
                        }

                        table {
                            width: 100%;
                            border-collapse: collapse;
                        }

                        th, td {
                            border: 1px solid #000;
                            padding: 3px;
                            text-align: center;
                            vertical-align: middle;
                            font-size:10px;
                        }

                        th {
                            font-weight: bold;
                            background: #f2f2f2;
                        }

                        .left {
                            text-align: left;
                        }

                        tr {
                            page-break-inside: avoid;
                        }

                .report-header {
                    display: table;
                    width: 100%;
                    table-layout: fixed;
                    margin: 10px 0 25px;
                    text-align: center;
                }

                .report-header .logo,
                .report-header .school-details {
                    display: table-cell;
                    vertical-align: middle;
                }

                .report-header .logo {
                    width: 15%;
                    text-align: center;
                }

                .report-header .logo img {
                    max-width: 85px;
                    max-height: 75px;
                    width: auto;
                    height: auto;
                    object-fit: contain;
                }

                .report-header .school-details {
                    width: 70%;
                }

                .trust-name {
                    font-size: 15px;
                    font-weight: bold;
                    margin-bottom: 5px;
                }

                .school-name {
                    font-size: 20px;
                    font-weight: bold;
                    text-transform: uppercase;
                    margin: 5px 0;
                }

                .report-title {
                    font-size: 16px;
                    font-weight: bold;
                    margin-top: 5px;
                }

                </style>
                </head>
                <body>

                <div style="
                    display:flex;
                    align-items:center;
                    justify-content:space-between;
                    width:100%;
                    box-sizing:border-box;
                    margin-bottom:40px;
                    margin-top:10px;
                ">

                    <img
                        src="${this.org_basic_data.trust_logo || ''}"
                        style="width:100px; height:90px; object-fit:contain; flex-shrink:0;"
                    />

                    <div style="flex:1; text-align:center; min-width:0;">
                        <h2>${this.org_basic_data.trust_name || ''}</h2>
                        <h2>${this.org_basic_data.school_name || ''}</h2>
                        <h2>Student Attendance Report</h2>
                    </div>

                    <img
                        src="${this.org_basic_data.school_logo || ''}"
                        style="width:100px; height:90px; object-fit:contain; flex-shrink:0;"
                    />

                </div>


                <table>
                    <thead>
                        <tr>
                            <th>Exam Plan</th>
                            <th>Academic Year</th>
                            <th>Semester</th>
                            <th>Start Date</th>
                            <th>End Date</th>
                            <th>Exam Conduct Type</th>
                            <th>Stream</th>
                            <th>Standard</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr>
                            <td>${this.scheduleHeader.exam_name || "-"}</td>
                            <td>${this.scheduleHeader.academic_year || "-"}</td>
                            <td>${this.scheduleHeader.semester || "-"}</td>
                            <td>${this.formatDate(this.scheduleHeader.start_date)}</td>
                            <td>${this.formatDate(this.scheduleHeader.end_date)}</td>
                            <td>${this.formatList(this.scheduleHeader.exam_conduct_types)}</td>
                            <td>${this.formatList(this.scheduleHeader.streams)}</td>
                            <td>${this.formatStandards(this.scheduleHeader.standards)}</td>
                        </tr>
                    </tbody>
                </table>

                <br>

                <table>
                    <thead>
                        <tr>
                            <th>S.No</th>
                            <th>PRN</th>
                            <th>Roll No</th>
                            <th>Student Name</th>
                            <th>Present(P)/
                                Absent(A)</th>
                            <th>Session No</th>
                            <th>Exam Date</th>
                            <th>Day</th>
                            <th>Slot No</th>
                            <th>From Time</th>
                            <th>To Time</th>
                            <th>Subject Code</th>
                            <th>Subject Name</th>
                            <th>Conduct Type</th>
                            <th>Room No</th>
                            <th>Bench No</th>
                            <th>Bench Side</th>
                            <th>Supervisor</th>
                            <th>Standard</th>
                            <th>Division</th>
                            <th>Stream</th>
                            <th>Exam Plan</th>
                            <th>Academic Year</th>
                            <th>Semester</th>
                        </tr>
                    </thead>
                    <tbody>
            `;

            this.attendanceReportList.forEach((item, index) => {
                html += `
                    <tr>
                        <td>${index + 1}</td>
                        <td>${item.prn || ""}</td>
                        <td>${item.rollno || ""}</td>
                        <td class="left">${item.student_name || ""}</td>
                        <td>${item.attendance_status || ""}</td>
                        <td>${item.session_number || ""}</td>
                        <td>${item.exam_date || ""}</td>
                        <td>${item.day || ""}</td>
                        <td>${item.slot_no || ""}</td>
                        <td>${item.from_time || ""}</td>
                        <td>${item.to_time || ""}</td>
                        <td>${item.subject_code || ""}</td>
                        <td class="left">${item.subject_name || ""}</td>
                        <td>${item.conduct_type || ""}</td>
                        <td>${item.room_number || ""}</td>
                        <td>${item.bench_no || ""}</td>
                        <td>${item.bench_side || ""}</td>
                        <td class="left">${item.supervisor_name || ""}</td>
                        <td>${item.standard || ""}</td>
                        <td>${item.division || ""}</td>
                        <td>${item.stream || ""}</td>
                        <td>${item.exam_plan_name || ""}</td>
                        <td>${item.academic_year || ""}</td>
                        <td>${item.semester || ""}</td>
                    </tr>
                `;
            });

            html += `
                    </tbody>
                </table>

                </body>
                </html>
            `;

            printWindow.document.open();
            printWindow.document.write(html);
            printWindow.document.close();

            printWindow.onload = function() {
                printWindow.focus();
                printWindow.print();
            };
        },

        showSnackbar(clr, msg) {
            this.snackbar = true;
            this.color = clr;
            this.snackbar_msg = msg;
        }
    }
};
</script>

<style scoped>
.v-text-field {
    padding-top: 0px;
    margin-top: 0px;
}

.simple-exam-table {
    width: 100%;
    border-collapse: collapse;
    background-color: #ffffff;
    text-align: center;
    border: 1px solid #dcdcdc;
}

.simple-exam-table th {
    background-color: #f2f2f2;
    color: #333333;
    font-weight: 600;
    font-size: 13px;
    padding: 10px 8px;
    border: 1px solid #dcdcdc;
}

.simple-exam-table td {
    padding: 10px 8px;
    font-size: 13px;
    color: #444444;
    border: 1px solid #dcdcdc;
}

.custom-row-height ::v-deep td {
    height: 52px !important;
    padding-top: 8px !important;
    padding-bottom: 8px !important;
    white-space: nowrap;
}

.custom-row-height ::v-deep th {
    height: 48px !important;
    white-space: nowrap;
}
</style>